<template>
    <div
        :style="localStyles"
        v-if="widget.WidgetID"
        :ref="`wrapperContainer-${widget.WidgetID}`"
        class="items-center flex rounded-lg justify-center p-0.5 min-h-full card-bg"
        :class="{'pulse-border': customColorByType}"
    >
        <div class="flex w-full justify-between flex-row absolute top-0.5 action-container h-10">
            <div
                v-if="showWidgetTitle"
                class="flex items-center"
            >
                <i class="icon-lg widget-header-title mx-2" :class="widget.WidgetTemplateIcon"/>
                <base-widget-title :title="widget.Title"/>
            </div>
            <div class="flex items-center widget-header-actions">
                <span v-if="showInfoButton" class="px-2" @click="onInfoClick">
                    <i class="vc-icon-info icon-lg widget-header-action cursor-help hover:text-primary"/>
                </span>
                <CardAction
                        class="rounded widget-header-action py-1-5 px-2 hover:text-primary"
                        :editable="editable"
                        v-on="$listeners"
                        :main-color="mainColor"
                />
            </div>
        </div>
        <div class="card-action_wrapper rounded min-h-full pt-2">
            <div class="p-2">
                <slot name="icon">
                    <component :is="cardIcon" class="status-icon text-primary" :style="cardValueIconStyles" />
                </slot>
            </div>
            <div class="p-2">
                <slot name="text">
                    <p
                        v-if="showText"
                        :style="cardTitleStyles"
                        class="font-bold card-text"
                        :class="{'pulse-text': customColorByType}"
                    >
                        {{ cardText }}
                    </p>
                </slot>
            </div>
            <div
                class="p-2"
            >
                <div>
                    <slot name="value">
                        <p
                            class="font-bold card-text"
                            :class="{'pulse-text': customColorByType}"
                            :style="cardValueStyles"
                        >
                            {{ cardValue }}
                        </p>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import get from 'lodash/get'
    import statusTypes from '@/enum/statusTypes'
    import { types, typeKeys } from '@/enum/queueCounters'
    
    export default {
        components: {
            CardAction: () => import('@/components/Cards/CardAction')
        },
        props: {
            editable: {
                type: Boolean,
                default: false
            },
            showText: {
                type: Boolean,
                default: false
            },
            cardIcon: {
                type: String,
                default: ''
            },
            cardText: {
                type: String,
                default: ''
            },
            cardValue: {
                type: [String, Number],
                default: ''
            },
            styles: {
                type: Object,
                default: () => ({})
            },
            layoutConfig: {
                type: Object,
                default: () => ({})
            },
            widget: {
                type: Object,
                default: () => ({})
            },
            usedDynamicStatuses: {
                type: Boolean,
                default: false
            },
            status: {
                type: [Number, String]
            },
            queue: {
                type: Boolean,
                default: false
            },
            useSoundAnimation: {
                type: Boolean,
                default: false
            },
            selectedType: {
                type: Number
            }
        },
        data() {
            return {
                isVertical: false,
                templateHelp: {},
                customColorByType: ''
            }
        },
        computed: {
            localStyles () {
                const layoutTextColor = getComputedStyle(document.documentElement).getPropertyValue('--mainTextColor-color')
                const layoutBgColor = getComputedStyle(document.documentElement).getPropertyValue('--colorWidgetBackground-color')
                const layoutBorderColor = getComputedStyle(document.documentElement).getPropertyValue('--colorWidgetBorder-color')

                const currentColor = this.mainColor && this.mainColor.color ? this.mainColor.color : layoutTextColor;
                const currentBg = this.styles.background !== layoutBgColor ? this.styles.background : layoutBgColor;
                const currentBorder = this.styles.border !== `border: 2px solid ${layoutBorderColor}` ? this.styles.border : `border: 2px solid ${layoutBorderColor}`;

                return {
                    ...this.styles,
                    '--mainTextColor-color':  currentColor,
                    'background': currentBg,
                    ...(this.customColorByType && {'--pulse-color': this.customColorByType}),
                    currentBorder
                }
            },
            mainColor() {
                return get(this.styles, 'color', '')
            },
            cardTitleStyles() {
                return {
                    fontSize: this.styles.titleFontSize
                }
            },
            cardValueStyles() {
                return {
                    fontSize: this.styles.valueFontSize,
                    ...(this.customColorByType && {'--pulse-color': this.customColorByType})
                }
            },
            showInfoButton() {
                return get(this.templateHelp, "Items", []).length > 0
            },
            cardValueIconStyles() {
                const color = this.usedDynamicStatuses && this.status !== '' ? this.styleForStatusIconColor(this.status, this.queue) : this.mainColor
                return {
                    ...color,
                    minWidth: this.styles.valueIconMinWidth
                }
            },
            showWidgetTitle() {
                return false
                // Show widget title, may need in future
                // return this.status === typeKeys.CALLERS_ID
            },
            getAccountStatuses () {
                return this.$store.getters['entities/accountStatuses']
            },
            warningSounds () {
                return this.$store.getters['entities/getWarningSounds']
            },
            errorSounds () {
                return this.$store.getters['entities/getErrorSounds']
            }
        },
        methods: {
            onInfoClick() {
                this.$emit('on-show-info')
            },
            getHelpByWidgetsTemplateID() {
                const templateId = get(this.widget, "TemplateID")
                if (!templateId) {
                    this.templateHelp = {}
                    return
                }

                const helpData = this.$store.getters['templatesCategory/getHelpByWidgetsTemplateID'](templateId)
                this.templateHelp = get(helpData, 'Help', {})
            },
            styleForStatusIconColor (status, queue) {
                const statusACtive = typeof status === 'object' ? 'StatusID' in  status ? status.StatusID : status.value : status
                const dynamicColor = this.getAccountStatuses.find(el => Number(el.StatusID) === Number(statusACtive))
                const color = dynamicColor && dynamicColor.ColorCode ? dynamicColor.ColorCode : queue ? Object.values(types)[statusACtive].color : statusTypes[statusACtive].color

                return {
                    '--status-svg-color': color
                }
            },
            getCurrentSoundValue (soundValue, type) {
                const sounds = type === 'warning'  ? this.warningSounds : this.errorSounds
                const currentSound = sounds.find(el => el.value === soundValue)
                if (!currentSound) {
                    return null
                }
                return currentSound.sound
            },
            soundAnimation () {
                if (Number(this.cardValue) === 0 || this.cardValue === '00:00') {
                    this.$store.dispatch('sound/removeSoundsFromQueue', `${this.cardText}-${this.widget.WidgetID}`)
                    this.customColorByType = ''
                    return
                }

                if (!this.widget || !this.widget.WidgetLayout) {
                    return
                }

                let soundItem = null
                if (this.selectedType === 0 && 'LimitMaxCounter' in this.widget.WidgetLayout && this.cardValue >= this.widget.WidgetLayout.LimitMaxCounter) {
                    const notificationData = {
                        notification_value: this.widget.WidgetLayout.LimitMaxCounter,
                        entity_name: this.cardText,
                        current_value: this.cardValue,
                        type: 'limit',
                        property: 'max counter'
                    }
                    soundItem = {
                        id: `${this.cardText}-${this.widget.WidgetID}-LimitMaxCounter`,
                        sound: this.getCurrentSoundValue(this.widget.WidgetLayout.soundLimit, 'limit'),
                        played: false,
                        isPlaying: false,
                        type: 'info',
                        timestamp: new Date().getTime(),
                        notificationHTML: this.$t('widget.soundBaseCardHtml', {...notificationData})
                    }
                } else if (this.selectedType === 0 && 'WarningMaxCounter' in this.widget.WidgetLayout && this.cardValue <= this.widget.WidgetLayout.LimitMaxCounter && this.cardValue >= this.widget.WidgetLayout.WarningMaxCounter) {
                    const notificationData = {
                        notification_value: this.widget.WidgetLayout.WarningMaxCounter,
                        entity_name: this.cardText,
                        current_value: this.cardValue,
                        type: 'warning',
                        property: 'max counter'
                    }
                    soundItem = {
                        id: `${this.cardText}-${this.widget.WidgetID}-WarningMaxCounter`,
                        sound: this.getCurrentSoundValue(this.widget.WidgetLayout.soundWarning, 'warning'),
                        played: false,
                        isPlaying: false,
                        type: 'warning',
                        timestamp: new Date().getTime(),
                        notificationHTML: this.$t('widget.soundBaseCardHtml', {...notificationData})
                    }
                }
                if (this.selectedType === 1 && 'LimitMaxTime' in this.widget.WidgetLayout && this.timeToSeconds(this.cardValue) >= this.widget.WidgetLayout.LimitMaxTime) {
                    const notificationData = {
                        notification_value: this.widget.WidgetLayout.LimitMaxTime,
                        entity_name: this.cardText,
                        current_value: this.cardValue,
                        type: 'limit',
                        property: 'max time'
                    }
                    soundItem = {
                        id: `${this.cardText}-${this.widget.WidgetID}-LimitMaxTime`,
                        sound: this.getCurrentSoundValue(this.widget.WidgetLayout.soundLimit, 'limit'),
                        played: false,
                        isPlaying: false,
                        type: 'info',
                        timestamp: new Date().getTime(),
                        notificationHTML: this.$t('widget.soundBaseCardHtml', {...notificationData})
                    }
                } else if (this.selectedType === 1 && 'WarningMaxTime' in this.widget.WidgetLayout && this.timeToSeconds(this.cardValue) <= this.widget.WidgetLayout.LimitMaxTime && this.timeToSeconds(this.cardValue) >= this.widget.WidgetLayout.WarningMaxTime) {
                    const notificationData = {
                        notification_value: this.widget.WidgetLayout.WarningMaxTime,
                        entity_name: this.cardText,
                        current_value: this.cardValue,
                        type: 'warning',
                        property: 'max time'
                    }
                    soundItem = {
                        id: `${this.cardText}-${this.widget.WidgetID}-WarningMaxTime`,
                        sound: this.getCurrentSoundValue(this.widget.WidgetLayout.soundWarning, 'warning'),
                        played: false,
                        isPlaying: false,
                        type: 'warning',
                        timestamp: new Date().getTime(),
                        notificationHTML: this.$t('widget.soundBaseCardHtml', {...notificationData})
                    }
                }

                if (soundItem) {
                    this.customColorByType = soundItem.type === 'warning' ? this.widget.WidgetLayout.warningColor : this.widget.WidgetLayout.limitColor
                    this.$store.dispatch('sound/setupSounds', soundItem)
                } else {
                    this.customColorByType = ''
                }
            },
            timeToSeconds(time) {
                const [minutes, seconds] = time.split(':');
                const timeSeconds = (parseInt(minutes) * 60) + parseInt(seconds)
                return timeSeconds
            }
        },
        mounted() {
            this.getHelpByWidgetsTemplateID()
        },
        beforeDestroy() {
            this.$store.dispatch('sound/removeSoundsFromQueue', `${this.cardText}-${this.widget.WidgetID}`)
        },
        watch: {
            cardValue: {
                handler () {
                    if (this.useSoundAnimation) {
                        this.soundAnimation()
                    }
                },
                immediate: true
            },
            widget: {
                deep: true,
                immediate: true,
                handler: function () {
                    if (this.useSoundAnimation) {
                        this.soundAnimation()
                    }
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
.card-action_wrapper {
    @apply flex items-center justify-center flex-wrap;
    
    &.edit-mode {
        top: 0;
        right: 0px;
    }
}
[dir="ltr"] .widget-header-actions {
    @apply mr-2;
    margin-left: auto;
}
[dir="rtl"] .widget-header-actions {
    @apply ml-2;
    margin-right: auto;
}
.card-text {
    color: var(--mainTextColor-color);
}
</style>

<style>
.pulse-text {
    -webkit-animation: color_text 1s infinite alternate;
    -moz-animation: color_text 1s infinite alternate;
    -ms-animation: color_text 1s infinite alternate;
    -o-animation: color_text 1s infinite alternate;
    animation: color_text 1s infinite alternate;
}
.pulse-border {
    -webkit-animation: color_border 1s infinite alternate;
    -moz-animation: color_border 1s infinite alternate;
    -ms-animation: color_border 1s infinite alternate;
    -o-animation: color_border 1s infinite alternate;
    animation: color_border 1s infinite alternate;
}


@keyframes color_border {
  to { border: 2px solid var(--pulse-color); }
}
@keyframes color_text {
    to { color: var(--pulse-color); }
}
@-webkit-keyframes color_border {
    to { border: 2px solid var(--pulse-color); }
}
@-webkit-keyframes color_text {
    to { color: var(--pulse-color); }
}

</style>